
/**
 * What a `SheetHandleCore` hears from its rows-side sheet, wired once by its
 * parse: the sheet's own structural events (which bump the core's
 * generation), the store -> cache bridge (a changed cell fires the cache a
 * renderer bound), and native cell arrival (a native install's rows and range
 * handed to the accessor). Each link owns its own state (the bridge's row
 * index, the arrival's pending rows and range) and returns its teardown; the
 * core keeps only the teardowns.
 */
import type { Option, Unsubscribe } from "@tento-lona/sheets";
import { NaiveDate } from "@tento-chrono";
import type { ListenerErrorSink } from "@tento-core/listener-errors";
import { Tracing } from "@tento-core/tracing";
import { RowLocalId, RowNetworkKey } from "@tento-lona/sheets";
import type { Sheet } from "@tento-lona/sheets/platform";
import type { CellAccessor } from "../cells/sheet-cell-accessor.ts";
import {
  type CellStoreFeed,
  CellStoreView,
  type StoreChange,
  type StoreChangeTarget,
} from "../cells/cell-store-view.ts";
import { Actor, NewRowInserted, SheetStructureChanged } from "../invalidation.ts";
import type { SheetRow } from "../rows/sheet-row.ts";
import { cellAccessKey } from "../sync/cell-access-key.ts";
import { DisposeFailures } from "./handle-errors.ts";
import { SheetDataSource } from "./sheet-data-source.ts";

/**
 * Fallback actor for store→handle bridge mirrors of untagged writes
 * (formula compute, sync replay). A singleton no plugin holds, so
 * `change.actor === cellActor` self-echo checks never match it.
 */
const STORE_BRIDGE_ACTOR = new Actor("store-bridge");
const NATIVE_ARRIVAL_ACTOR = new Actor("native-cell-arrival");

/**
 * Keep a core's generation honest about its rows-side sheet: the rows-side
 * sheet mutates its own tree without going through the core, so its
 * structural events (`NewRowInserted` for one of its own rows,
 * `SheetStructureChanged` for itself) bump the core's generation — except the
 * echo of a mirror write the core is making (`core.mirroring()`). Returns the
 * unsubscribe.
 */
export function watchRowsSideStructure(
  rowsSheetForStructure: Sheet,
  core: {
    /** The core is writing a structural change it already accounted for. */
    readonly mirroring: () => boolean;
    /** The core's generation now. */
    readonly generation: () => number;
    /** Drop the resolved tree and bump the generation. */
    readonly bump: () => void;
  },
): Unsubscribe {
  // Structural coherence: the rows-side sheet mutates its OWN tree
  // (alias re-expansion on preference change, sync-log reconcile, …)
  // without going through this handle, but `SheetViewModel.joinedTree`
  // caches the joined tree keyed on `handle.generation`. Without a
  // bump the DOM rebinds against the STALE joined rows — e.g. the
  // weather relocation kept painting the old location until a scroll
  // because the re-keyed child's new RowCellCache was never re-bound
  // (ux-test 2026-07-02_dataref-weather-phase-f_c477bb; learning
  // 2026-07-02-weather-relocation-stale-until-scroll). Bump the
  // generation on rows-side structural events so the next `tree`
  // read rebuilds. Bumping is idempotent local state (no emission,
  // no loop); `NewRowInserted` from OTHER sheets sharing the app
  // sink is filtered by membership.
  return rowsSheetForStructure.invalidationSink
    .subscribe((event) => {
      if (event instanceof NewRowInserted) {
        // SCOPE first, membership second. System-row local ids
        // (`system(header)`, `system(surface|footer)`, `system(footer)`)
        // are interned per USER, not per sheet
        // (`RowLocalId.Registry.scopedKey`), so ANOTHER sheet's chrome
        // swap — `SheetHandle.parse`'s `rowsSheet.replaceSystemRows`, or a
        // `SheetViewModel.make` rebuild — resolves the very same
        // `RowLocalId`s this sheet holds and sails through the membership
        // check below. Landing inside a promote's await window that cost
        // six foreign bumps and tripped `cow-promote-rerender`
        // (ux-test cow-promote-home-fe-be-aligned, run
        // 2026-09-07_…_93b70b: genBefore 0 → genAfter 6, all six rows
        // owned by the `:calendar` sheet's chrome). `NewRowInserted`
        // carries the owning sheet's id — ask what its coalesced
        // `SheetStructureChanged` sibling already asks.
        if (!event.concerns(rowsSheetForStructure.networkKey)) return;
        if (rowsSheetForStructure.peekRow(event.rowId) == null) return;
      } else if (event instanceof SheetStructureChanged) {
        // Scope filter — sheet A's structural storm must not rebuild
        // THIS handle's joined tree (the sibling of NewRowInserted's
        // scope + membership checks above).
        if (!event.concerns(rowsSheetForStructure.id)) return;
      } else {
        return;
      }
      // Echo of a mirror write this handle is making right now (see
      // `mirroringStructure`): the change is already reflected in the
      // handle's own tree — and already generation-bumped when it needed
      // to be — so re-bumping here only forces a redundant rebuild.
      if (core.mirroring()) {
        Tracing.log("handle/structural-echo-skipped", {
          event: event instanceof NewRowInserted
            ? "row-inserted"
            : "structure",
          row: event instanceof NewRowInserted
            ? event.rowId.toString()
            : null,
          generation: core.generation(),
        });
        return;
      }
      core.bump();
      // Relocation-repaint diagnostics (session breadcrumb): proves a
      // rows-side structural event reached the handle and bumped the
      // cached-tree generation. Pairs with `viewmodel/tree-rebuild`.
      // NOTE: literal discriminator, NOT `constructor.name` — the
      // production bundle minifies class names to single letters.
      Tracing.log("handle/structural-bump", {
        event: event instanceof NewRowInserted
          ? "row-inserted"
          : "structure",
        row: event instanceof NewRowInserted
          ? event.rowId.toString()
          : null,
        generation: core.generation(),
      });
    });
}

/**
 * The store -> cache bridge of a core's rows-side store (`bridgeStore`): a
 * changed key fires the cache bound for its row, else the shared cache under
 * the row's cell-access key (a local write binds it; a server read waits for
 * a reader). `rows` lists the core's rows; a key is found through an index of
 * them by identity, rebuilt when the list changes. Returns the unlink.
 */
export function linkStoreBridge(
  bridgeStore: CellStoreFeed<RowLocalId>,
  rows: () => readonly SheetRow.Handle[],
  cells: CellAccessor,
  listenerErrors: ListenerErrorSink,
): () => void {
    // The row a store key names, through one index of the accessor's rows
    // by identity, rebuilt when its list is another array or another length
    // (a push, a delete); a scan of the rows per mirrored change made an
    // open O(cells × rows) (279M row reads at 1600 rows). A key the index
    // does not hold is looked for in the list as before.
    let byId: Option<{
      readonly list: readonly SheetRow.Handle[];
      readonly length: number;
      readonly rows: Map<RowLocalId, SheetRow.Handle>;
    }> = null;
    const rowFor = (key: RowLocalId): Option<SheetRow.Handle> => {
      const list = rows();
      if (byId == null || byId.list !== list || byId.length !== list.length) {
        const rows = new Map<RowLocalId, SheetRow.Handle>();
        for (const r of list) if (!rows.has(r.id)) rows.set(r.id, r);
        byId = { list, length: list.length, rows };
      }
      const hit = byId.rows.get(key);
      if (hit != null && hit.id === key) return hit;
      return list.find((r) => r.id === key) ?? null;
    };
    // The cache a changed key shows: the one bound for the row, else the
    // SHARED cache under the row's cell-access key. A row nobody bound a
    // cache for has no listener to fire.
    return CellStoreView.link<RowLocalId>(
      bridgeStore,
      (key, change) => {
        const bound = cells.peekRowHandleOf(key);
        if (bound?.view?.clientScoped) return null;
        if (bound != null) return bound;
        const sourceRow = rowFor(key);
        if (!sourceRow) return null;
        const accessKey = RowNetworkKey.fromWire(cellAccessKey(sourceRow.id.toString(), sourceRow.lookupKey));
        // A local write binds the row's cache (its writer's actor is the
        // cell's last actor from now on); a server read's cells wait for a
        // reader to bind it.
        return change.server
          ? cells.peekRowHandle(accessKey)
          : cells.rowHandle(accessKey);
      },
      STORE_BRIDGE_ACTOR,
      SheetDataSource.SERVER_FETCH_ACTOR,
      listenerErrors,
    );
}

/**
 * Native cell arrival: a native `SourceRow` install updates the physical
 * stores (`bridgeStore`, the client's `canonicalCells`) without passing
 * through the accessor's fetch / write notifications, so its rows and range
 * are collected (deferred while a store batch defers) and handed to
 * `cells.notifyNativeArrival` — not once `disposed()`. Returns the teardown.
 */
export function linkNativeArrival(
  bridgeStore: Option<CellStoreFeed<RowLocalId>>,
  canonicalCells: CellStoreFeed<string>,
  rowsSide: Sheet,
  cells: CellAccessor,
  disposed: () => boolean,
  listenerErrors: ListenerErrorSink,
): () => void {
  // Native SourceRow installs update the physical stores without passing
  // through the accessor's SDK fetch/write notification paths.
  const pendingRows = new Set<string>();
  const pendingTimepointRows = new Set<string>();
  let pendingRange: Option<NaiveDate.Range> = null;
  let deferred = 0;
  const flushArrival = (): void => {
    if (deferred > 0 || pendingRange == null) return;
    const rows = [...pendingRows];
    const certifiedDateRows = new Set(rows.filter(row => !pendingTimepointRows.has(row)));
    const range = pendingRange;
    pendingRows.clear();
    pendingTimepointRows.clear();
    pendingRange = null;
    if (!disposed()) cells.notifyNativeArrival(rows, range, certifiedDateRows);
  };
  const target: StoreChangeTarget = {
    deferNotifications: () => {
      deferred++;
      let released = false;
      return () => {
        if (released) return;
        released = true;
        deferred--;
        flushArrival();
      };
    },
    storeChanged: (change) => {
      if (disposed()) return;
      const range = pendingRange;
      pendingRange = range == null ? change.range : new NaiveDate.Range(
        range.start.dse < change.range.start.dse ? range.start : change.range.start,
        range.end.dse > change.range.end.dse ? range.end : change.range.end,
      );
      flushArrival();
    },
  };
  // Actor alone is insufficient: untagged writes outside asInstall are local.
  // SDK bulk installs have SERVER_FETCH_ACTOR and already notify on completion.
  const nativeArrival = (change: StoreChange): boolean => !disposed()
    && change.install && !change.server && change.actor === NATIVE_ARRIVAL_ACTOR;
  const nativeLocalUnsub = bridgeStore == null ? null : CellStoreView.link<RowLocalId>(
    bridgeStore,
    (key, change) => {
      if (!nativeArrival(change)) return null;
      const row = rowsSide.peekRow(key);
      if (row == null) return null;
      pendingRows.add(row.id.toString());
      if (change.kind === "timepoint") pendingTimepointRows.add(row.id.toString());
      return target;
    },
    NATIVE_ARRIVAL_ACTOR,
    SheetDataSource.SERVER_FETCH_ACTOR,
    listenerErrors,
  );
  const nativeCanonicalUnsub = CellStoreView.link<string>(
    canonicalCells,
    (key, change) => {
      if (!nativeArrival(change)) return null;
      const rows = rowsSide.peekAllByLookupKey(key);
      if (rows.length === 0) return null;
      for (const row of rows) {
        pendingRows.add(row.id.toString());
        if (change.kind === "timepoint") pendingTimepointRows.add(row.id.toString());
      }
      return target;
    },
    NATIVE_ARRIVAL_ACTOR,
    SheetDataSource.SERVER_FETCH_ACTOR,
    listenerErrors,
  );
  return () => {
    pendingRows.clear();
    pendingTimepointRows.clear();
    pendingRange = null;
    const failures = new DisposeFailures();
    if (nativeLocalUnsub != null) failures.run(nativeLocalUnsub);
    failures.run(nativeCanonicalUnsub);
    failures.rethrow("SheetHandle native cell arrival teardown failed");
  };
}
